import { CommandResponse, GamePlayConfig, QueryResponse } from 'shared-services'
import { PlayingGame, playingGameNull } from '../models/playing-game'
import { PlayingGameDto } from '../dtos/playing-game.dto'
import { playingGameMapper } from '../mappers/playing-game.mapper'
import { PlayTrickErrorCode } from '../error-codes/play-trick.error-code'
import { Trick } from '../models/trick'
import { GetGameErrorCode } from '../error-codes/get-game.error-code'
import { GamePlayGameId } from '../models/game-play-game-id'

export class PlayingGameService {
  public constructor(private readonly gamePlayConfig: GamePlayConfig) {}

  public async getPlayingGame(
    gamePlayGameId: GamePlayGameId,
  ): Promise<QueryResponse<PlayingGame, GetGameErrorCode>> {
    const gamePlayId = gamePlayGameId.gamePlayId
    const gameId = gamePlayGameId.gameId
    const query = `gamePlayId=${gamePlayId}&gameId=${gameId}`

    const response = await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/playing-game/v1/playing-game?${query}`,
      {
        credentials: 'include',
      },
    )

    const playingGameResponse = (await response.json()) as QueryResponse<
      PlayingGameDto,
      GetGameErrorCode
    >

    const result = playingGameResponse.isSuccessful
      ? playingGameMapper.mapToPlayingGame(playingGameResponse.result)
      : playingGameNull

    return {
      errorCode: playingGameResponse.errorCode,
      errorCodes: playingGameResponse.errorCodes,
      isSuccessful: playingGameResponse.isSuccessful,
      result,
    }
  }

  public async playTrick(
    gamePlayGameId: GamePlayGameId,
    trick: Trick,
  ): Promise<CommandResponse<PlayTrickErrorCode>> {
    const gamePlayId = gamePlayGameId.gamePlayId
    const gameId = gamePlayGameId.gameId

    const body = {
      gamePlayId,
      gameId,
      ...trick,
    }

    const playTrickResponse = await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/playing-game/v1/play-trick`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return (await playTrickResponse.json()) as CommandResponse<PlayTrickErrorCode>
  }

  public async declareRestOfTricksAreMine(
    gamePlayGameId: GamePlayGameId,
  ): Promise<void> {
    const gamePlayId = gamePlayGameId.gamePlayId
    const gameId = gamePlayGameId.gameId

    const body = {
      gamePlayId,
      gameId,
    }

    await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/playing-game/v1/declare-rest-of-tricks-are-mine`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
  }

  public async declareNoneOfTricksAreMine(
    gamePlayGameId: GamePlayGameId,
  ): Promise<void> {
    const gamePlayId = gamePlayGameId.gamePlayId
    const gameId = gamePlayGameId.gameId

    const body = {
      gamePlayId,
      gameId,
    }

    await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/playing-game/v1/declare-none-of-tricks-are-mine`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
  }

  public async respondToDeclarerHasRestOfTricks(
    gamePlayGameId: GamePlayGameId,
    hasAgreed: boolean,
  ): Promise<void> {
    const gamePlayId = gamePlayGameId.gamePlayId
    const gameId = gamePlayGameId.gameId

    const body = {
      gamePlayId,
      gameId,
      hasAgreed,
    }

    await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/playing-game/v1/respond-to-declarer-has-rest-of-tricks`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
  }

  public async respondToDeclarerHasNoneOfTricks(
    gamePlayGameId: GamePlayGameId,
    hasAgreed: boolean,
  ): Promise<void> {
    const gamePlayId = gamePlayGameId.gamePlayId
    const gameId = gamePlayGameId.gameId

    const body = {
      gamePlayId,
      gameId,
      hasAgreed,
    }

    await fetch(
      `${this.gamePlayConfig.apiBaseUrl}/playing-game/v1/respond-to-declarer-has-none-of-tricks`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
  }
}
